/**
 * advance-detection.ts — per-tick "who sees whom" pass.
 *
 * Runs inside advanceTick after movement/jumps finalise positions (and
 * before combat, once combat lands). Rebuilds GameState.teamVision —
 * per-team Maps of VisibilityRecord, one entry per currently-or-recently
 * observed enemy ship.
 *
 * Ships mid-jump (systemId === null) and own-team ships are skipped
 * entirely. Renderers treat their own ships as always-visible and never
 * consult teamVision for them; keeping the map free of own-ship rows
 * stops it from doubling as a weird redundant source of truth.
 *
 * Algorithm (per tick):
 *   1. Walk each team's map — expire records past LAST_KNOWN_GRACE_TICKS,
 *      and reset `seenVia` to null on every survivor. The detection pass
 *      below sets it back to 'vision' or 'scan' for anything re-spotted
 *      this tick; anything untouched coasts as a ghost (seenVia=null)
 *      until the grace window elapses and the record is pruned.
 *   2. Bucket ships by systemId.
 *   3. For each team, for each system, for each non-team target:
 *        - walk every team scanner and check visionSees / scanSees
 *        - vision hit wins over scan hit (full icon reveal > dot blip)
 *        - if any scanner triggers either channel, upsert the record
 *          with fresh position + tick + channel; otherwise leave the
 *          existing record alone so the grace window ghosts-then-expires
 *          it
 *
 * The model is fully deterministic — no RNG draws, no probability rolls.
 * Vision is binary (range gate), Scan is binary (range gate + Detection >
 * Stealth). Cloak hard-gates both. Firing-breaks-stealth zeroes effective
 * Stealth for a short window so a firing ship lights up under scan.
 *
 * Iteration order is still locked (team asc → system asc → target asc) so
 * the pass is reproducible across hosts for save/replay diffing.
 *
 * Complexity: O(systems × teams × ships_in_system²) per tick. At v1 scale
 * (≤3 empires, ~2 ships per empire per system, 12 systems) that's
 * trivial. MECHANICS §1.3.3 flags a broadphase (spatial bucketing) as the
 * planned optimisation for late-game fleet sizes.
 */

import {
  DEFAULT_DETECTION_BALANCE,
  type DetectionBalance,
} from './balance-detection';
import { collectTeamScannersForSystem, type ScannerRow } from './scanners';
import { stealthEmpireBonus } from './research-effects';
import {
  MINEFIELDS_CLOAKED_TECH,
  MINEFIELD_CHASSIS,
} from './balance-space-structures';
import {
  effectiveStealth,
  scanSees,
  visionSees,
  type TargetView,
} from './detection';
import { auraBonusesForShip } from './auras';
import type {
  GameState,
  ShipInstance,
  ShipTemplate,
  SystemId,
  VisibilityRecord,
} from '../state/types';

/**
 * Advance team vision by one tick. Mutates state.teamVision.
 *
 * v3M10.1 — `lastKnownGraceTicks` and `firingBreaksStealthTicks` are now
 * sourced from `state.balanceData.detection` (the xlsx-driven catalogue).
 * The legacy `DEFAULT_DETECTION_BALANCE` record is folded in for the
 * remaining DetectionBalance fields (visionRangeScale, scanRangeScale,
 * detectionTickInterval) so callers without state still get a sensible
 * default for the stateless helper paths.
 */
export function advanceDetection(
  state: GameState,
  balanceArg: DetectionBalance = DEFAULT_DETECTION_BALANCE,
): void {
  const balance: DetectionBalance = {
    ...balanceArg,
    lastKnownGraceTicks: state.balanceData.detection.lastKnownGraceTicks,
    firingBreaksStealthTicks: state.balanceData.detection.firingBreaksStealthTicks,
    cloakEnergyDrainPerTick: state.balanceData.detection.cloakEnergyDrainPerTick,
  };
  // Balance knob lets future profiling throttle this to every-Nth tick.
  if (state.tick % balance.detectionTickInterval !== 0) return;

  // 1) Grace-window sweep + reset seenVia on every survivor. The detection
  //    pass below sets it back to 'vision' / 'scan' for anything re-spotted.
  //    Records untouched stay with seenVia=null and their previous
  //    lastSeenTick — renderer draws them as ghosts until grace expires.
  for (const teamMap of state.teamVision.values()) {
    for (const [shipId, rec] of teamMap) {
      if (state.tick - rec.lastSeenTick > balance.lastKnownGraceTicks) {
        teamMap.delete(shipId);
      } else {
        rec.seenVia = null;
      }
    }
  }

  // 2) Bucket ships by system. Skip mid-jump (systemId === null) — ships in
  //    hyperspace neither see nor are seen by the in-system detection pass.
  //    (v2: a separate pseudo-bucket will host mid-jump ships vs deep-space
  //    sensor structures; not wired yet.)
  const bySystem = new Map<SystemId, ShipInstance[]>();
  for (const ship of state.ships.values()) {
    if (ship.systemId === null) continue;
    let bucket = bySystem.get(ship.systemId);
    if (!bucket) {
      bucket = [];
      bySystem.set(ship.systemId, bucket);
    }
    bucket.push(ship);
  }
  const systemIds = [...bySystem.keys()].sort((a, b) => a - b);

  // 3) Iterate teams in ascending TeamId for stable ordering.
  const teamIds = [...state.teamVision.keys()].sort((a, b) => a - b);

  for (const teamId of teamIds) {
    const teamMap = state.teamVision.get(teamId)!;

    for (const systemId of systemIds) {
      const shipsInSystem = bySystem.get(systemId)!;

      // Targets = ships in this system owned by a different team.
      const targets: ShipInstance[] = [];
      for (const s of shipsInSystem) {
        if (s.ownerEmpireId !== teamId) targets.push(s);
      }
      if (targets.length === 0) continue;

      // Scanners come from the central helper so cross-system structure
      // scanners can be plumbed in v2 without touching this loop.
      const scanners = collectTeamScannersForSystem(state, teamId, systemId, balance);
      if (scanners.length === 0) continue;

      targets.sort((a, b) => a.id - b.id);

      for (const target of targets) {
        // v3M2.3b — Cloaked Mines tech: a Minefield owned by an empire
        // that has researched 'Minefields - Cloaked Mines' is fully
        // invisible to other teams until the first mine triggers
        // (`mineCloakRevealed = true`). Skip scanner evaluation entirely
        // so no teamVision row is added for unrevealed cloaked fields.
        if (target.chassis === MINEFIELD_CHASSIS && !target.mineCloakRevealed) {
          const targetOwner = state.empires.get(target.ownerEmpireId);
          if (
            targetOwner &&
            targetOwner.researchedTechs.has(MINEFIELDS_CLOAKED_TECH)
          ) {
            continue;
          }
        }
        const tmplT = templateFor(state, target);
        if (!tmplT) continue;

        // MFix7.2 — Stealth Mk research adds a flat empire-wide bonus
        // to stealthBase (Non-combat: Stealth category).
        // MFix8.5 — friendly aura `stealthBonus` (e.g. Magician) is
        // additive on top of the empire bonus. The aura helper is a
        // cheap NO_AURA fast-path when no eligible emitters cover this
        // ship.
        const targetEmpire = state.empires.get(target.ownerEmpireId);
        const stealthBonus = targetEmpire
          ? stealthEmpireBonus(state, targetEmpire)
          : 0;
        const auraStealthBonus = auraBonusesForShip(state, target).stealthBonus;
        const targetView: TargetView = {
          sx: target.sx,
          sy: target.sy,
          cloaked: target.cloaked,
          stealthEffective: effectiveStealth(
            tmplT.stealthBase + stealthBonus + auraStealthBonus,
            target.lastFiredTick,
            state.tick,
            balance,
          ),
        };

        // Walk scanners — vision wins over scan (full icon beats dot blip).
        // Short-circuit the moment any vision hit lands; only bother with
        // scan if no scanner has vision on this target.
        let channel: 'vision' | 'scan' | null = null;
        for (const scanner of scanners) {
          if (visionSees(scanner, targetView, balance)) {
            channel = 'vision';
            break;
          }
          if (channel === null && scanSees(scanner, targetView, balance)) {
            channel = 'scan';
            // Don't break — a later scanner might still have vision on it.
          }
        }

        if (channel === null) continue;

        // Visible this tick — upsert the record with fresh position + tick.
        const existing = teamMap.get(target.id);
        if (existing) {
          existing.lastSeenTick = state.tick;
          existing.lastSeenSystemId = target.systemId!;
          existing.lastSeenSx = target.sx;
          existing.lastSeenSy = target.sy;
          existing.seenVia = channel;
        } else {
          const rec: VisibilityRecord = {
            shipId: target.id,
            lastSeenTick: state.tick,
            lastSeenSystemId: target.systemId!,
            lastSeenSx: target.sx,
            lastSeenSy: target.sy,
            seenVia: channel,
          };
          teamMap.set(target.id, rec);
        }
      }
    }
  }
}

function templateFor(
  state: GameState,
  ship: ShipInstance,
): ShipTemplate | undefined {
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return undefined;
  return state.shipTemplates.get(empire.raceId + ':' + ship.chassis);
}

// Re-export for callers that want the scanner type without reaching into
// ./scanners. Kept as a type re-export so tree-shaking isn't hampered.
export type { ScannerRow };
